import React, { useEffect, useState } from 'react';
import Swal from 'sweetalert2';
import Barcode from 'react-barcode';
import 'boxicons/css/boxicons.min.css';
import { useAuth } from '../../Constant/Auth';
import * as Constants from '../../Constant/constant';

import {
  getUserDataById,
  getErrorMessage
} from '../../api/api';

const ParentLibraryCard = () => {
  const { user } = useAuth();

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchProfile = async () => {
    try {
      if (!user?.id) return;

      setLoading(true);
      const data = await getUserDataById(user.id);
      setProfile(data || user);
    } catch (error) {
      console.error('Library card profile error:', error);
      Swal.fire('Error', getErrorMessage(error), 'error');
      setProfile(user);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const getImageUrl = (path) => {
    if (!path) return null;
    if (String(path).startsWith('http')) return path;

    const cleanPath = String(path).replace(/\\/g, '/').replace(/^\/+/, '');
    return `${Constants.IMAGE_BASE_URL}/${cleanPath}`;
  };

  if (loading || !profile) {
    return (
      <div className="container-fluid mt-3">
        <div className="text-center py-5">Loading...</div>
      </div>
    );
  }

  // ── Field logic mirrored from the librarian Library Card ──
  const nameOf = (m) =>
    m?.full_name ||
    m?.member_name ||
    m?.parent_name ||
    m?.name ||
    `${m?.first_name || ''} ${m?.last_name || ''}`.trim() ||
    '-';

  const mobileOf = (m) => m?.mobile || m?.phone || m?.mobile_no || '-';

  const typeOf = (m) =>
    m?.member_type ||
    m?.membership_type ||
    m?.member_source ||
    m?.membership_source ||
    'school_parent';

  const statusOf = (m) => {
    const s =
      m?.status ?? m?.membership_status ?? m?.active_membership_status;
    if (typeof s === 'string') return s;
    if (s === 0 || s === '0') return 'inactive';
    return 'active';
  };

  const initials = (n) =>
    (String(n).match(/\b\w/g) || []).slice(0, 2).join('').toUpperCase() || 'M';

  /*
    Same card-code logic as the librarian card so the scanned value matches:
    membership_no if present, otherwise {prefix}-{libraryId}-{memberId}
    (school_parent => SP, outsider => OUT, student => STU, staff => STF, else LIB)
  */
  const getMemberCardCode = (member) => {
    const directCode =
      member?.membership_no ||
      member?.membershipNo ||
      member?.card_no ||
      member?.library_card_no ||
      member?.active_membership_no;

    if (directCode) return String(directCode).trim();

    const type = String(typeOf(member) || '').toLowerCase();
    const libId = member?.library_id || user?.library_id || 1;
    const memberId = member?.member_id || member?.id || '';

    let prefix = 'LIB';
    if (type.includes('school_parent') || type.includes('parent')) prefix = 'SP';
    else if (type.includes('outsider')) prefix = 'OUT';
    else if (type.includes('student')) prefix = 'STU';
    else if (type.includes('staff') || type.includes('teacher')) prefix = 'STF';

    return `${prefix}-${libId}-${memberId}`;
  };

  const name = nameOf(profile);
  const img = profile.user_img || profile.image || profile.profile_image;
  const cardCode = getMemberCardCode(profile);

  return (
    <div className="container-fluid mt-3 lc-page">
      <style>{`
        .lc-page h3 { font-weight: 800; color: #1e293b; }

        /* center the card horizontally on the page */
        .lc-page .print-area { display: flex; justify-content: center; }
        .lc-page .lc-card { margin: 0 auto; }

        @media print {
          @page { margin: 10mm; }
          body * { visibility: hidden; }
          .print-area, .print-area * { visibility: visible; }
          .print-area { position: absolute; top: 0; left: 0; width: 100%; }
          .no-print { display: none !important; }
          .lc-card { page-break-inside: avoid; box-shadow: none; animation: none; }
        }

        .lc-card, .lc-card * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        .lc-card {
          width: 330px; max-width: 100%; background: #fff; border-radius: 18px; overflow: hidden;
          border: 1px solid #e7eef7; box-shadow: 0 12px 30px rgba(15,23,42,.16);
          animation: lcPop .55s cubic-bezier(.18,.89,.32,1.28) both;
        }
        .lc-head { display: flex; align-items: center; justify-content: center; gap: 8px; padding: 14px 16px 10px; }
        .lc-head .lc-logo {
          width: 26px; height: 26px; border-radius: 7px; color: #fff; font-size: 15px;
          background: linear-gradient(135deg, #f59e0b, #14b8a6);
          display: flex; align-items: center; justify-content: center;
        }
        .lc-head .lc-uni { font-size: 15px; font-weight: 800; color: #0f172a; letter-spacing: .02em; }
        .lc-photo {
          position: relative; text-align: center; padding: 16px 0 30px;
          background: linear-gradient(160deg, #22d3ee 0%, #0ea5e9 55%, #2563eb 125%);
          border-radius: 0 0 46% 46% / 0 0 30px 30px;
        }
        .lc-avatar {
          position: absolute; left: 50%; bottom: 0; transform: translate(-50%, 50%);
          width: 92px; height: 92px; border-radius: 50%; background: #fff; border: 4px solid #fff;
          box-shadow: 0 6px 16px rgba(2,28,55,.25); overflow: hidden;
          display: flex; align-items: center; justify-content: center;
          font-size: 32px; font-weight: 800; color: #0ea5e9;
          animation: lcAvatar .6s ease both .15s;
        }
        .lc-avatar img { width: 100%; height: 100%; object-fit: cover; }
        .lc-body { padding: 52px 18px 6px; text-align: center; }
        .lc-name { font-size: 19px; font-weight: 800; color: #0f172a; letter-spacing: .03em; }
        .lc-dept { font-size: 11.5px; font-weight: 700; color: #0ea5e9; text-transform: uppercase; letter-spacing: .06em; margin-top: 3px; }
        .lc-course { font-size: 10.5px; color: #64748b; text-transform: uppercase; letter-spacing: .05em; margin-top: 1px; }
        .lc-grid2 { display: grid; grid-template-columns: 1fr 1fr; gap: 9px 14px; text-align: left; margin: 14px 4px 0; }
        .lc-item { min-width: 0; }
        .lc-item.full { grid-column: 1 / -1; }
        .lc-item label { display: block; font-size: 8.5px; color: #94a3b8; font-weight: 700; letter-spacing: .09em; text-transform: uppercase; margin-bottom: 1px; }
        .lc-item span { font-size: 12px; color: #0f172a; font-weight: 600; word-break: break-word; }
        .lc-sign { display: flex; align-items: flex-end; justify-content: space-between; gap: 10px; padding: 14px 16px 12px; }
        .lc-reg { text-align: center; }
        .lc-reg .sig { font-family: 'Segoe Script','Brush Script MT',cursive; font-size: 16px; color: #1e3a8a; line-height: 1; }
        .lc-reg .line { border-top: 1px solid #cbd5e1; margin-top: 3px; padding-top: 3px; font-size: 8.5px; color: #64748b; letter-spacing: .1em; text-transform: uppercase; }
        .lc-qr { background: #fff; padding: 3px; border: 1px solid #e2e8f0; border-radius: 6px; line-height: 0; }
        .lc-bar { height: 24px; background: linear-gradient(90deg, #0f172a, #1e3a8a 60%, #0ea5e9); display: flex; align-items: center; justify-content: center; }
        .lc-bar span { color: #cbd5e1; font-size: 9px; letter-spacing: .14em; text-transform: uppercase; }

        @keyframes lcPop { from { opacity: 0; transform: translateY(22px) scale(.96); } to { opacity: 1; transform: translateY(0) scale(1); } }
        @keyframes lcAvatar { from { opacity: 0; transform: translate(-50%, 50%) scale(.5); } to { opacity: 1; transform: translate(-50%, 50%) scale(1); } }
        @media (prefers-reduced-motion: reduce) { .lc-card, .lc-avatar { animation: none !important; } }
      `}</style>

      <div className="pt-page-header no-print">
        <div className="pt-page-header__left">
          <span className="pt-page-header__icon">
            <i className="fas fa-id-card" />
          </span>
          <div>
            <h3 className="pt-page-header__title">Library Card</h3>
            <div className="pt-page-header__subtitle">Your library membership card</div>
          </div>
        </div>
        <div className="pt-page-header__actions">
          <button className="btn btn-primary" onClick={() => window.print()}>
            <i className="bx bx-printer me-1" /> Print Card
          </button>
        </div>
      </div>

      <div className="print-area">
        <div className="lc-card">
          <div className="lc-head">
            <div className="lc-logo"><i className="bx bxs-book" /></div>
            <div className="lc-uni">JAIPURIA LIBRARY</div>
          </div>

          <div className="lc-photo">
            <div className="lc-avatar">
              {img ? <img src={getImageUrl(img)} alt={name} /> : <span>{initials(name)}</span>}
            </div>
          </div>

          <div className="lc-body">
            <div className="lc-name">{name}</div>
            {/* <div className="lc-dept">{typeOf(profile)}</div> */}
            <div className="lc-course">Library Membership Card</div>

            <div className="lc-grid2">
              <div className="lc-item"><label>Card No</label><span>{cardCode}</span></div>
              <div className="lc-item"><label>Unique ID</label><span>{profile.unique_id || '-'}</span></div>
              <div className="lc-item"><label>Phone</label><span>{mobileOf(profile)}</span></div>
              <div className="lc-item full"><label>Email</label><span>{profile.email || '-'}</span></div>
            </div>
          </div>

          <div className="lc-sign">
            <div className="lc-reg">
              <div className="sig">Authorised</div>
              <div className="line">Issuing Librarian</div>
            </div>
            <div className="lc-qr">
              <Barcode
                value={cardCode}
                format="CODE128"
                width={1.1}
                height={34}
                fontSize={9}
                margin={0}
                background="transparent"
              />
            </div>
          </div>

          <div className="lc-bar"><span>Jaipuria Group of Educational Institutions</span></div>
        </div>
      </div>
    </div>
  );
};

export default ParentLibraryCard;